import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton,
  Stack, Tab, Tabs, TextField, Tooltip, Typography, alpha,
} from '@mui/material'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined'
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined'
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined'
import AlternateEmailRoundedIcon from '@mui/icons-material/AlternateEmailRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import ForumRoundedIcon from '@mui/icons-material/ForumRounded'
import MarkChatUnreadOutlinedIcon from '@mui/icons-material/MarkChatUnreadOutlined'
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined'
import QuestionMarkRoundedIcon from '@mui/icons-material/QuestionMarkRounded'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { Feedback, FeedbackType } from '@cmms/types'

import { PageShell } from '../../layouts/PageShell'
import { UserAvatar } from '../../components/UserAvatar'
import { useConfirm } from '../../components/ConfirmDialog'
import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'

export function HelpPage() {
  const [tab, setTab] = useState<'feedback' | 'inbound-email' | 'other'>('feedback')

  const toolbar = (
    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
      minHeight: 38,
      '& .MuiTab-root': {
        minHeight: 38, textTransform: 'none', fontSize: 13, fontWeight: 600,
        letterSpacing: '-0.005em', color: 'text.secondary', px: 0, mr: 3.5,
        '&.Mui-selected': { color: 'primary.main' },
      },
      '& .MuiTabs-indicator': { height: 2 },
    }}>
      <Tab value="feedback" icon={<MarkChatUnreadOutlinedIcon sx={{ fontSize: 16 }} />}
        iconPosition="start" label="Submit feedback" />
      <Tab value="inbound-email" icon={<AlternateEmailRoundedIcon sx={{ fontSize: 16 }} />}
        iconPosition="start" label="Email-to-request" />
      <Tab value="other" icon={<MenuBookOutlinedIcon sx={{ fontSize: 16 }} />}
        iconPosition="start" label="More resources" />
    </Tabs>
  )

  return (
    <PageShell title="Help & Support" toolbar={toolbar}>
      {tab === 'feedback' ? <FeedbackTab />
        : tab === 'inbound-email' ? <InboundEmailGuide />
        : <OtherResources />}
    </PageShell>
  )
}

// ── Submit feedback ─────────────────────────────────────────────────

const FEEDBACK_TYPES: Array<{
  value: FeedbackType; label: string; description: string; icon: React.ReactNode; color: string
}> = [
  { value: 'bug', label: 'Bug report', description: 'Something is broken or behaving unexpectedly',
    icon: <BugReportRoundedIcon sx={{ fontSize: 17 }} />, color: '#DC2626' },
  { value: 'feature_request', label: 'Feature request', description: "There's something you wish this app could do",
    icon: <LightbulbOutlinedIcon sx={{ fontSize: 17 }} />, color: '#7C3AED' },
  { value: 'process_feedback', label: 'This is confusing', description: 'A workflow is harder to follow than it should be',
    icon: <RouteOutlinedIcon sx={{ fontSize: 17 }} />, color: '#D97706' },
  { value: 'question', label: 'Question', description: 'Something you\'re not sure how to do',
    icon: <QuestionMarkRoundedIcon sx={{ fontSize: 17 }} />, color: '#2563EB' },
]

const STATUS_META: Record<Feedback['status'], { label: string; color: string; bg: string }> = {
  submitted:    { label: 'Submitted',    color: '#2563EB', bg: '#EFF6FF' },
  acknowledged: { label: 'Acknowledged', color: '#D97706', bg: '#FFFBEB' },
  resolved:     { label: 'Resolved',     color: '#059669', bg: '#ECFDF5' },
}

function FeedbackTab() {
  const qc = useQueryClient()
  const location = useLocation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [type, setType] = useState<FeedbackType>('bug')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [screenshots, setScreenshots] = useState<Array<{ file: File; previewUrl: string; uploadedUrl?: string; uploading: boolean }>>([])

  const mineQ = useQuery({
    queryKey: ['feedback', 'mine'],
    queryFn: () => api.feedback.listMine(),
  })
  const mine: Feedback[] = Array.isArray(mineQ.data) ? mineQ.data : []

  async function handleFilesPicked(files: FileList | null) {
    if (!files || files.length === 0) return
    const remaining = 6 - screenshots.length
    if (remaining <= 0) {
      toast.error('Up to 6 screenshots per submission')
      return
    }
    const picked = Array.from(files).slice(0, remaining)
    const entries = picked.map((file) => ({
      file, previewUrl: URL.createObjectURL(file), uploading: true,
    }))
    setScreenshots((prev) => [...prev, ...entries])

    for (const entry of entries) {
      try {
        const { url } = await api.feedback.uploadImage(entry.file)
        setScreenshots((prev) => prev.map((s) =>
          s === entry ? { ...s, uploadedUrl: url, uploading: false } : s))
      } catch (e) {
        toast.error(e instanceof ApiError ? e.message : "Couldn't upload screenshot")
        setScreenshots((prev) => prev.filter((s) => s !== entry))
      }
    }
  }

  function removeScreenshot(entry: { previewUrl: string }) {
    setScreenshots((prev) => prev.filter((s) => s.previewUrl !== entry.previewUrl))
    URL.revokeObjectURL(entry.previewUrl)
  }

  const submitM = useMutation({
    mutationFn: () => api.feedback.create({
      type,
      title: title.trim(),
      description: description.trim(),
      screenshot_urls: screenshots.map((s) => s.uploadedUrl).filter((u): u is string => !!u),
      page_context: location.pathname,
    }),
    onSuccess: () => {
      toast.success("Thanks — we've got it.")
      setTitle(''); setDescription('')
      screenshots.forEach((s) => URL.revokeObjectURL(s.previewUrl))
      setScreenshots([])
      qc.invalidateQueries({ queryKey: ['feedback', 'mine'] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't submit — try again"),
  })

  const anyUploading = screenshots.some((s) => s.uploading)
  const canSubmit = title.trim().length > 0 && description.trim().length > 0
    && !anyUploading && !submitM.isPending

  return (
    <Stack spacing={3}>
      {/* Submission form */}
      <Box sx={{
        borderRadius: 3, border: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper', p: { xs: 2.5, md: 3 },
      }}>
        <Typography sx={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em', mb: 0.5 }}>
          Tell us what's going on
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2.5 }}>
          Bug, feature idea, or "this workflow makes no sense" — whatever it is, we read every one.
        </Typography>

        {/* Type picker */}
        <Box sx={{
          display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 1, mb: 2.5,
        }}>
          {FEEDBACK_TYPES.map((t) => {
            const selected = type === t.value
            return (
              <Box key={t.value} onClick={() => setType(t.value)}
                sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 1.25,
                  p: 1.5, borderRadius: 2, cursor: 'pointer',
                  border: '1.5px solid', borderColor: selected ? t.color : 'divider',
                  bgcolor: selected ? alpha(t.color, 0.06) : 'transparent',
                  transition: 'all 120ms ease',
                  '&:hover': { borderColor: selected ? t.color : alpha(t.color, 0.4) },
                }}>
                <Box sx={{
                  width: 30, height: 30, borderRadius: 1.5, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: alpha(t.color, selected ? 0.18 : 0.1), color: t.color,
                }}>
                  {t.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: selected ? t.color : 'text.primary' }}>
                    {t.label}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.4 }}>
                    {t.description}
                  </Typography>
                </Box>
              </Box>
            )
          })}
        </Box>

        <Stack spacing={2}>
          <TextField
            label="Title" size="small" fullWidth
            value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Short summary — e.g. Can't upload photos on Android"
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            label="Description" size="small" fullWidth multiline minRows={4}
            value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="What happened, what did you expect, and any steps to reproduce it."
            inputProps={{ maxLength: 5000 }}
          />

          {/* Screenshots */}
          <Box>
            <input ref={fileInputRef} type="file" accept="image/*" multiple hidden
              onChange={(e) => { handleFilesPicked(e.target.files); e.target.value = '' }} />
            <Button
              size="small" variant="outlined"
              startIcon={<AttachFileRoundedIcon sx={{ fontSize: 15 }} />}
              onClick={() => fileInputRef.current?.click()}
              disabled={screenshots.length >= 6}
              sx={{ fontSize: 12.5, textTransform: 'none', borderRadius: 1.5 }}
            >
              Attach screenshots
            </Button>
            {screenshots.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
                {screenshots.map((s) => (
                  <Box key={s.previewUrl} sx={{
                    position: 'relative', width: 64, height: 64,
                    borderRadius: 1.5, overflow: 'hidden',
                    border: '1px solid', borderColor: 'divider',
                  }}>
                    <Box component="img" src={s.previewUrl} sx={{
                      width: '100%', height: '100%', objectFit: 'cover',
                      opacity: s.uploading ? 0.4 : 1,
                    }} />
                    {s.uploading && (
                      <CircularProgress size={18} sx={{
                        position: 'absolute', top: '50%', left: '50%',
                        marginTop: '-9px', marginLeft: '-9px',
                      }} />
                    )}
                    <IconButton size="small" onClick={() => removeScreenshot(s)}
                      sx={{
                        position: 'absolute', top: 2, right: 2, width: 18, height: 18,
                        bgcolor: 'rgba(0,0,0,0.55)', color: '#fff',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                      }}>
                      <CloseRoundedIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          <Box>
            <Button
              variant="contained" disabled={!canSubmit}
              onClick={() => submitM.mutate()}
              sx={{ fontSize: 13, fontWeight: 700, borderRadius: 2, px: 3 }}
            >
              {submitM.isPending ? 'Sending…' : 'Send feedback'}
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* My submissions */}
      <Box>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.secondary', mb: 1.25 }}>
          Your submissions {mine.length > 0 && `(${mine.length})`}
        </Typography>
        {mineQ.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={22} />
          </Box>
        ) : mine.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: 'text.disabled', fontStyle: 'italic' }}>
            You haven't submitted anything yet.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {mine.map((f) => <FeedbackRow key={f.id} feedback={f} />)}
          </Stack>
        )}
      </Box>
    </Stack>
  )
}

function FeedbackRow({ feedback }: { feedback: Feedback }) {
  const [open, setOpen] = useState(false)
  const typeMeta = FEEDBACK_TYPES.find((t) => t.value === feedback.type)
  const statusMeta = STATUS_META[feedback.status]
  const hasUnreadReply = feedback.last_message_sender_type === 'super_admin'

  return (
    <>
      <Box onClick={() => setOpen(true)} sx={{
        border: '1px solid', borderColor: 'divider', borderRadius: 2,
        bgcolor: 'background.paper', cursor: 'pointer', transition: 'border-color 120ms ease',
        '&:hover': { borderColor: 'primary.main' },
      }}>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 1.75, py: 1.25 }}>
          <Box sx={{
            width: 26, height: 26, borderRadius: 1.25, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(typeMeta?.color ?? '#64748B', 0.1), color: typeMeta?.color ?? '#64748B',
          }}>
            {typeMeta?.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{feedback.title}</Typography>
              {hasUnreadReply && (
                <Box sx={{ width: 6, height: 6, borderRadius: 99, bgcolor: 'primary.main', flexShrink: 0 }} />
              )}
            </Stack>
            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
              {new Date(feedback.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            </Typography>
          </Box>
          <Chip size="small" label={statusMeta.label} sx={{
            height: 22, fontSize: 11, fontWeight: 700,
            bgcolor: statusMeta.bg, color: statusMeta.color,
          }} />
          <ChevronRightRoundedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
        </Stack>
      </Box>
      {open && (
        <FeedbackConversationDialog feedback={feedback} typeMeta={typeMeta} onClose={() => setOpen(false)} />
      )}
    </>
  )
}

function dayLabel(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString()
  if (sameDay(d, today)) return 'Today'
  if (sameDay(d, yesterday)) return 'Yesterday'
  return d.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric',
    year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  })
}

/// Full-height chat dialog for one submission — the "dedicated
/// conversation" view rather than an inline accordion. The original
/// submission (title/description/screenshots) renders as the first
/// item in the scrollable thread — it's the opening post, not a row
/// in `feedback_messages` — followed by day-grouped replies and a
/// composer pinned to the bottom of the dialog.
function FeedbackConversationDialog({
  feedback, typeMeta, onClose,
}: {
  feedback: Feedback
  typeMeta: typeof FEEDBACK_TYPES[number] | undefined
  onClose: () => void
}) {
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [reply, setReply] = useState('')
  const [attachments, setAttachments] = useState<Array<{ file: File; previewUrl: string; uploadedUrl?: string; uploading: boolean }>>([])
  const scrollRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const statusMeta = STATUS_META[feedback.status]

  const messagesQ = useQuery({
    queryKey: ['feedback', 'messages', feedback.id],
    queryFn: () => api.feedback.listMessages(feedback.id),
    // Live updates arrive over the websocket (see useChatSocket); this
    // is just a fallback in case the socket is momentarily down.
    refetchInterval: 15000,
  })
  const messages = Array.isArray(messagesQ.data) ? messagesQ.data : []

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages.length])

  async function handleFilesPicked(files: FileList | null) {
    if (!files || files.length === 0) return
    const remaining = 6 - attachments.length
    if (remaining <= 0) {
      toast.error('Up to 6 images per message')
      return
    }
    const picked = Array.from(files).slice(0, remaining)
    const entries = picked.map((file) => ({ file, previewUrl: URL.createObjectURL(file), uploading: true }))
    setAttachments((prev) => [...prev, ...entries])

    for (const entry of entries) {
      try {
        const { url } = await api.feedback.uploadImage(entry.file)
        setAttachments((prev) => prev.map((a) => a === entry ? { ...a, uploadedUrl: url, uploading: false } : a))
      } catch (e) {
        toast.error(e instanceof ApiError ? e.message : "Couldn't upload image")
        setAttachments((prev) => prev.filter((a) => a !== entry))
      }
    }
  }

  function removeAttachment(entry: { previewUrl: string }) {
    setAttachments((prev) => prev.filter((a) => a.previewUrl !== entry.previewUrl))
    URL.revokeObjectURL(entry.previewUrl)
  }

  const anyUploading = attachments.some((a) => a.uploading)

  const sendM = useMutation({
    mutationFn: () => api.feedback.sendMessage(
      feedback.id, reply.trim(),
      attachments.map((a) => a.uploadedUrl).filter((u): u is string => !!u),
    ),
    onSuccess: () => {
      setReply('')
      attachments.forEach((a) => URL.revokeObjectURL(a.previewUrl))
      setAttachments([])
      qc.invalidateQueries({ queryKey: ['feedback', 'messages', feedback.id] })
      qc.invalidateQueries({ queryKey: ['feedback', 'mine'] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't send reply"),
  })

  const canSend = (reply.trim().length > 0 || attachments.length > 0) && !anyUploading && !sendM.isPending

  const deleteMessageM = useMutation({
    mutationFn: (msgId: string) => api.feedback.deleteMessage(feedback.id, msgId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feedback', 'messages', feedback.id] }),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't delete message"),
  })

  const deleteThreadM = useMutation({
    mutationFn: () => api.feedback.delete(feedback.id),
    onSuccess: () => {
      toast.success('Submission withdrawn')
      qc.invalidateQueries({ queryKey: ['feedback', 'mine'] })
      onClose()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't delete submission"),
  })

  async function handleDeleteThread() {
    const ok = await confirm({
      title: 'Withdraw this submission?',
      message: 'This deletes the entire conversation, including every reply. This cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger',
    })
    if (ok) deleteThreadM.mutate()
  }

  let lastDay = ''

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm"
      PaperProps={{ sx: { height: '82vh', maxHeight: 640, display: 'flex', flexDirection: 'column' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: 1.5, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(typeMeta?.color ?? '#64748B', 0.1), color: typeMeta?.color ?? '#64748B',
        }}>
          {typeMeta?.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700 }} noWrap>{feedback.title}</Typography>
        </Box>
        <Chip size="small" label={statusMeta.label} sx={{
          height: 22, fontSize: 11, fontWeight: 700,
          bgcolor: statusMeta.bg, color: statusMeta.color,
        }} />
        <Tooltip title="Withdraw submission" arrow>
          <IconButton size="small" onClick={handleDeleteThread} disabled={deleteThreadM.isPending}>
            <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose}>
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent ref={scrollRef} dividers sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {/* Opening post */}
        <Box sx={{ pb: 1.5, mb: 1.5, borderBottom: '1px dashed', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {feedback.description}
          </Typography>
          {feedback.screenshot_urls.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
              {feedback.screenshot_urls.map((url) => (
                <Box key={url} component="a" href={url} target="_blank" rel="noreferrer"
                  sx={{ width: 56, height: 56, borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                  <Box component="img" src={url} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        {messagesQ.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={18} />
          </Box>
        ) : messages.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: 'text.disabled', fontStyle: 'italic' }}>
            No replies yet — send the first message below.
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {messages.map((m) => {
              const day = dayLabel(m.created_at)
              const showDivider = day !== lastDay
              lastDay = day
              return (
                <Box key={m.id}>
                  {showDivider && <DayDivider label={day} />}
                  <ChatBubble fromMe={m.sender_type === 'user'} typeColor={typeMeta?.color ?? '#64748B'}
                    senderLabel={m.sender_type === 'super_admin' ? 'Support' : (m.sender_name ?? 'You')}
                    body={m.body} attachmentUrls={m.attachment_urls} timestamp={m.created_at}
                    onDelete={m.sender_type === 'user' ? () => deleteMessageM.mutate(m.id) : undefined} />
                </Box>
              )
            })}
          </Stack>
        )}
      </DialogContent>

      {/* Composer — pinned to the bottom of the dialog, not the scroll area */}
      <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        {attachments.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {attachments.map((a) => (
              <Box key={a.previewUrl} sx={{
                position: 'relative', width: 48, height: 48,
                borderRadius: 1.25, overflow: 'hidden', border: '1px solid', borderColor: 'divider',
              }}>
                <Box component="img" src={a.previewUrl} sx={{
                  width: '100%', height: '100%', objectFit: 'cover', opacity: a.uploading ? 0.4 : 1,
                }} />
                {a.uploading && (
                  <CircularProgress size={16} sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-8px', marginLeft: '-8px' }} />
                )}
                <IconButton size="small" onClick={() => removeAttachment(a)} sx={{
                  position: 'absolute', top: 1, right: 1, width: 16, height: 16,
                  bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                }}>
                  <CloseRoundedIcon sx={{ fontSize: 11 }} />
                </IconButton>
              </Box>
            ))}
          </Stack>
        )}
        <Stack direction="row" spacing={1}>
          <input ref={fileInputRef} type="file" accept="image/*" multiple hidden
            onChange={(e) => { handleFilesPicked(e.target.files); e.target.value = '' }} />
          <IconButton size="small" onClick={() => fileInputRef.current?.click()}
            disabled={attachments.length >= 6}
            sx={{ alignSelf: 'flex-end', mb: 0.25 }}>
            <AttachFileRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <TextField
            size="small" fullWidth multiline maxRows={4} autoFocus
            placeholder="Message support…"
            value={reply} onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && canSend) {
                e.preventDefault()
                sendM.mutate()
              }
            }}
            sx={{ '& .MuiOutlinedInput-root': { fontSize: 12.5 } }}
          />
          <Button
            variant="contained" size="small"
            disabled={!canSend}
            onClick={() => sendM.mutate()}
            sx={{ fontSize: 12, fontWeight: 700, borderRadius: 1.5, flexShrink: 0, alignSelf: 'flex-end' }}
          >
            Send
          </Button>
        </Stack>
      </Box>
    </Dialog>
  )
}

function DayDivider({ label }: { label: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
    </Stack>
  )
}

/// Shared chat-bubble presentation — used by both the org-user Help
/// page thread and (in spirit) the super-admin detail panel. Kept as
/// two separate call sites rather than one cross-feature import since
/// the surrounding data (Feedback vs SuperAdminFeedback) differs, but
/// the bubble visuals themselves are copied verbatim so a thread looks
/// identical from either side of the conversation.
function ChatBubble({
  fromMe, senderLabel, body, attachmentUrls, timestamp, typeColor, onDelete,
}: {
  fromMe: boolean
  senderLabel: string
  body: string
  attachmentUrls?: string[]
  timestamp: string
  typeColor: string
  onDelete?: () => void
}) {
  return (
    <Stack direction={fromMe ? 'row-reverse' : 'row'} spacing={1} alignItems="flex-end"
      className="chat-bubble-row" sx={{ '&:hover .chat-bubble-delete': { opacity: 1 } }}>
      <UserAvatar name={senderLabel} size={26}
        sx={{ flexShrink: 0, bgcolor: fromMe ? typeColor : '#64748B' }} />
      {onDelete && (
        <Tooltip title="Delete message" arrow>
          <IconButton size="small" onClick={onDelete} className="chat-bubble-delete"
            sx={{ opacity: 0, transition: 'opacity 120ms ease', alignSelf: 'center', width: 22, height: 22 }}>
            <DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
      <Box sx={{ maxWidth: '76%' }}>
        <Typography sx={{
          fontSize: 10.5, fontWeight: 700, color: 'text.disabled', mb: 0.35,
          textAlign: fromMe ? 'right' : 'left',
        }}>
          {senderLabel} · {new Date(timestamp).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
        </Typography>
        <Box sx={{
          px: 1.5, py: 1, borderRadius: 2,
          bgcolor: fromMe ? alpha(typeColor, 0.1) : 'action.hover',
          border: '1px solid', borderColor: fromMe ? alpha(typeColor, 0.25) : 'divider',
        }}>
          {attachmentUrls && attachmentUrls.length > 0 && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: body ? 0.75 : 0 }}>
              {attachmentUrls.map((url) => (
                <Box key={url} component="a" href={url} target="_blank" rel="noreferrer"
                  sx={{ width: 72, height: 72, borderRadius: 1.25, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                  <Box component="img" src={url} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
              ))}
            </Stack>
          )}
          {body && (
            <Typography sx={{ fontSize: 12.5, color: 'text.primary', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
              {body}
            </Typography>
          )}
        </Box>
      </Box>
    </Stack>
  )
}

// ── Email-to-request guide ─────────────────────────────────────────

function InboundEmailGuide() {
  // The address is the only piece that varies per org. Anonymous /
  // non-authenticated viewers (or roles without inbound_email:manage)
  // see a placeholder — the steps are identical.
  const cfgQ = useQuery({
    queryKey: ['inbound-email', 'config'],
    queryFn:  () => api.inboundEmail.getConfig(),
    retry:    false,
  })
  const address = cfgQ.data?.inbound_address ?? 'support+<your-org-slug>@bacsys.net'
  const localPart = cfgQ.data?.inbound_local_part ?? 'support'
  const domain = cfgQ.data?.inbound_domain ?? 'bacsys.net'
  const orgSlug = cfgQ.data?.org_slug ?? '<your-org-slug>'

  function copyAddress() {
    navigator.clipboard.writeText(address).then(() => toast.success('Address copied'))
  }

  return (
    <Stack spacing={3}>
      {/* Hero — the address + a short pitch */}
      <Box sx={{
        position: 'relative', overflow: 'hidden',
        borderRadius: 3, border: '1px solid',
        borderColor: (t) => alpha(t.palette.primary.main, 0.2),
        background: (t) => `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.08)} 0%, ${alpha(t.palette.primary.main, 0.02)} 100%)`,
        p: { xs: 3, md: 4 },
      }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 1.75, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
            color: 'primary.contrastText',
            boxShadow: (t) => `0 6px 16px -4px ${alpha(t.palette.primary.main, 0.5)}`,
          }}>
            <AlternateEmailRoundedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.7,
              textTransform: 'uppercase', color: 'primary.main' }}>
              Email-to-request
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em' }}>
              Customers can email us to open a request
            </Typography>
          </Box>
        </Stack>
        <Typography sx={{ fontSize: 14, color: 'text.secondary', lineHeight: 1.65, mb: 2.5, maxWidth: 720 }}>
          Share your team's dedicated address with customers and partners. Every email
          arriving at it is automatically logged as a maintenance request and routed
          to the right inbox — no portal login required.
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}
          sx={{
            display: 'inline-flex', maxWidth: '100%',
            p: 1.25, borderRadius: 2,
            bgcolor: 'background.paper',
            border: '1px dashed', borderColor: (t) => alpha(t.palette.primary.main, 0.4),
          }}>
          <Typography sx={{
            fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: 'primary.dark',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
            maxWidth: { xs: '60vw', md: 480 },
          }}>
            {address}
          </Typography>
          <Tooltip title="Copy address" arrow>
            <IconButton size="small" onClick={copyAddress}
              sx={{ color: 'primary.main' }}>
              <ContentCopyRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Two-column: steps + sample mail */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1.1fr' },
        gap: 3,
      }}>
        {/* Steps */}
        <Box sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper', p: { xs: 2.5, md: 3 } }}>
          <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.7,
            textTransform: 'uppercase', color: 'text.disabled', mb: 1.5 }}>
            How it works
          </Typography>
          <Stack spacing={1.75}>
            <Step n={1}
              title="Share your address with customers"
              body={<>
                Send your team's dedicated address —{' '}
                <Code>{`${localPart}+${orgSlug}@${domain}`}</Code> — to anyone who needs to
                open a maintenance request. They don't need a login.
              </>} />
            <Step n={2}
              title="Customer sends an email"
              body={<>
                The subject line becomes the request title; the message body becomes the
                description. Attachments are saved to the request. Use a normal email
                client — there's nothing special to install.
              </>} />
            <Step n={3}
              title="We acknowledge instantly"
              body={<>
                We reply with a confirmation that includes the request code
                (<Code>REQ-00042</Code>). Replies stay threaded to the same request, so
                the conversation lives in one place.
              </>} />
            <Step n={4}
              title="Forgot the +slug? We still try"
              body={<>
                Org admins can add accepted sender domains under{' '}
                <Code>Settings → Inbound Email</Code>. Emails from those domains route
                to your org automatically, even without the suffix.
              </>} />
          </Stack>
        </Box>

        {/* Sample email */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.7,
            textTransform: 'uppercase', color: 'text.disabled', mb: 1.25 }}>
            Sample email
          </Typography>
          <Box sx={{
            borderRadius: 3, overflow: 'hidden',
            border: '1px solid', borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: (t) => t.palette.mode === 'light'
              ? '0 8px 24px -10px rgba(15,23,42,0.12)'
              : '0 8px 24px -10px rgba(0,0,0,0.4)',
          }}>
            {/* Faux mail-client header */}
            <Stack direction="row" alignItems="center" spacing={0.75}
              sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider',
                bgcolor: (t) => alpha(t.palette.text.primary, 0.04) }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#EF4444' }} />
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#F59E0B' }} />
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10B981' }} />
              <Box sx={{ flex: 1 }} />
              <EmailRoundedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
              <Typography sx={{ fontSize: 10.5, color: 'text.disabled', fontWeight: 700 }}>
                New message
              </Typography>
            </Stack>
            {/* Headers */}
            <Box sx={{ p: 2 }}>
              <MailHeader k="From" v="jane@acme.com" />
              <MailHeader k="To"   v={address} mono />
              <MailHeader k="Subject" v="AC unit on 3rd floor not cooling" />
              <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mt: 1, mb: 1.5 }} />
              <Typography sx={{ fontSize: 13.5, lineHeight: 1.6, color: 'text.primary',
                whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
{`Hi team,

The AC unit in conference room 3B has been making a rattling noise
since this morning and the room isn't cooling below 28°C. We have a
client meeting tomorrow at 10am.

Could someone take a look before then?

Thanks,
Jane Doe
Facilities — Acme Bangalore`}
              </Typography>
            </Box>
            {/* Outcome strip */}
            <Stack direction="row" alignItems="center" spacing={1}
              sx={{ px: 2, py: 1.25, bgcolor: (t) => alpha(t.palette.success.main, 0.08),
                borderTop: '1px solid', borderColor: (t) => alpha(t.palette.success.main, 0.2) }}>
              <CheckCircleRoundedIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography sx={{ fontSize: 12, color: 'success.dark', fontWeight: 700 }}>
                Becomes <Box component="span" sx={{ fontFamily: 'monospace' }}>REQ-00042</Box>{' '}
                — "AC unit on 3rd floor not cooling" — submitted, awaiting review.
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* Tips strip */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
        gap: 2,
      }}>
        <Tip icon={<ForumRoundedIcon />} title="Replies stay threaded"
          body="Customer + team replies to our acknowledgement append to the same request — no duplicate tickets." />
        <Tip icon={<LockOutlinedIcon />} title="Per-org routing"
          body="The +slug suffix tells us which workspace the email belongs to. Without it, sender-domain fallback handles known partners." />
        <Tip icon={<HelpOutlineOutlinedIcon />} title="Need a different address?"
          body="Your org admin can change which inbound mailbox you use. Reach out to support if you'd like a vanity address." />
      </Box>
    </Stack>
  )
}

// ── Other resources (placeholder for the coming-soon stuff) ────────

function OtherResources() {
  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
        gap: 2 }}>
        <HelpCard icon={<MenuBookOutlinedIcon />} color="#0D9488"
          title="Documentation" description="Browse guides for every module — assets, work orders, inventory and more." />
        <HelpCard icon={<PlayCircleOutlineOutlinedIcon />} color="#7C3AED"
          title="Video Tutorials" description="Step-by-step walkthroughs to get up and running quickly." />
        <HelpCard icon={<SchoolOutlinedIcon />} color="#2563EB"
          title="Getting Started" description="A quick-start guide for new users covering setup, roles and your first work order." />
        <HelpCard icon={<ForumOutlinedIcon />} color="#D97706"
          title="Community Forum" description="Ask questions, share tips and connect with other maintenance teams." />
        <HelpCard icon={<BugReportOutlinedIcon />} color="#DC2626"
          title="Report a Bug" description="Found something broken? Let us know and we'll fix it fast." />
        <HelpCard icon={<SupportAgentOutlinedIcon />} color="#059669"
          title="Contact Support" description="Email hello@bacsys.ai for direct help during business hours." />
      </Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{
        p: 3, borderRadius: 2.5, bgcolor: (t) => `${t.palette.primary.main}08`,
        border: '1px solid', borderColor: (t) => `${t.palette.primary.main}20`,
      }}>
        <HelpOutlineOutlinedIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Need more help?</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Full documentation, video tutorials and an in-app chat assistant are on the way.
          </Typography>
        </Box>
        <Chip size="small" label="Coming soon" sx={{
          height: 22, fontSize: 11, fontWeight: 800,
          bgcolor: 'primary.main', color: 'primary.contrastText',
        }} />
      </Stack>
    </Stack>
  )
}

// ── Small sub-components ───────────────────────────────────────────

function Step({ n, title, body }: { n: number; title: string; body: React.ReactNode }) {
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.5}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
        color: 'primary.contrastText',
        fontSize: 12, fontWeight: 800,
      }}>
        {n}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, pt: 0.15 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.35, mb: 0.4 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.6 }}>
          {body}
        </Typography>
      </Box>
    </Stack>
  )
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <Box component="code" sx={{
      bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
      px: 0.6, py: 0.1, borderRadius: 0.75,
      fontFamily: 'monospace', fontSize: 12.5, fontWeight: 700,
      color: 'primary.dark',
      whiteSpace: 'nowrap',
    }}>
      {children}
    </Box>
  )
}

function MailHeader({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <Stack direction="row" spacing={1.25} sx={{ mb: 0.4 }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.disabled',
        textTransform: 'uppercase', letterSpacing: 0.6, minWidth: 60, pt: 0.3 }}>
        {k}
      </Typography>
      <Typography sx={{
        fontSize: 13, color: 'text.primary',
        fontFamily: mono ? 'monospace' : 'inherit',
        fontWeight: mono ? 700 : 500,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {v}
      </Typography>
    </Stack>
  )
}

function Tip({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <Box sx={{
      p: 2, borderRadius: 2.5,
      border: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper',
    }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.85 }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: 1.25,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
          color: 'primary.main',
          '& .MuiSvgIcon-root': { fontSize: 16 },
        }}>
          {icon}
        </Box>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{title}</Typography>
      </Stack>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.6 }}>
        {body}
      </Typography>
    </Box>
  )
}

function HelpCard({ icon, color, title, description }: {
  icon: React.ReactNode; color: string; title: string; description: string
}) {
  return (
    <Box sx={{
      p: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', gap: 1.5,
    }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: '50%',
        background: `linear-gradient(135deg, ${color}, ${color}BB)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', '& .MuiSvgIcon-root': { fontSize: 22 },
      }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.25 }}>{title}</Typography>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.5 }}>{description}</Typography>
      </Box>
    </Box>
  )
}
